import { isInputEventKind } from "@dopejs/pingo-editing";
export const WORKER_PROTOCOL_VERSION = 14;
export function isRenderWorkerInboundMessage(value) {
    if (!isRecord(value) || !isPositiveU32(value.sessionId))
        return false;
    switch (value.kind) {
        case "pingo:prepare":
            return isPositiveU32(value.abiVersion) && isPositiveU32(value.protocolVersion);
        case "pingo:resize":
            return (isPositiveFinite(value.devicePixelRatio) &&
                isPositiveFinite(value.width) &&
                isPositiveFinite(value.height));
        case "pingo:activate":
            return (isWorkerMode(value.mode) &&
                isPositiveFinite(value.devicePixelRatio) &&
                isPositiveFinite(value.width) &&
                isPositiveFinite(value.height) &&
                isRecord(value.canvas) &&
                typeof value.rasterCache === "boolean" &&
                typeof value.incrementalPicturesEnabled === "boolean" &&
                typeof value.reducedMotion === "boolean" &&
                (value.mode === "post-message" ||
                    (isSharedArrayBuffer(value.ringBuffer) && isSharedArrayBuffer(value.inputRingBuffer))));
        case "pingo:clock-anchor":
            return isPositiveU32(value.sequence) && isFiniteNumber(value.timestamp);
        case "pingo:input":
            return value.bytes instanceof Uint8Array;
        case "pingo:input-wake":
            return true;
        case "pingo:reduced-motion":
            return typeof value.reduced === "boolean";
        case "pingo:layout-geometry-active":
        case "pingo:painted-text-active":
            return typeof value.active === "boolean";
        case "pingo:media-frame":
            return (isPositiveU32(value.resourceId) &&
                isRecord(value.source) &&
                (value.path === "image-bitmap" || value.path === "video-frame"));
        case "pingo:shutdown":
            return true;
        default:
            return false;
    }
}
export function isRenderWorkerInboundEnvelope(value) {
    if (!isRecord(value))
        return false;
    return (value.kind === "pingo:prepare" ||
        value.kind === "pingo:resize" ||
        value.kind === "pingo:activate" ||
        value.kind === "pingo:clock-anchor" ||
        value.kind === "pingo:input" ||
        value.kind === "pingo:input-wake" ||
        value.kind === "pingo:media-frame" ||
        value.kind === "pingo:reduced-motion" ||
        value.kind === "pingo:layout-geometry-active" ||
        value.kind === "pingo:painted-text-active" ||
        value.kind === "pingo:shutdown");
}
export function isRenderWorkerOutboundMessage(value) {
    if (!isRecord(value) || !isPositiveU32(value.sessionId))
        return false;
    switch (value.kind) {
        case "pingo:prepared":
            return (isRecord(value.capabilities) &&
                typeof value.capabilities.offscreenCanvas === "boolean" &&
                typeof value.capabilities.sharedArrayBuffer === "boolean");
        case "pingo:ready":
            return isWorkerMode(value.mode);
        case "pingo:frame":
            return isFrameReport(value.report);
        case "pingo:clock-metrics":
            return isClockMetrics(value.metrics);
        case "pingo:virtual-refill":
            return Array.isArray(value.requests) && value.requests.every(isVirtualRefillRange);
        case "pingo:edit-transaction":
            return isEditTransaction(value.transaction);
        case "pingo:structure-request":
            return isStructureRequest(value.request);
        case "pingo:document-selection":
            return isDocumentSelectionReport(value.report);
        case "pingo:event-transaction":
            return isEventTransaction(value.transaction);
        case "pingo:non-passive-regions":
            return Array.isArray(value.regions) && value.regions.every(isNonPassiveRegion);
        case "pingo:editing-geometry":
            return isEditingGeometryFrame(value.frame);
        case "pingo:semantics":
            return Array.isArray(value.nodes) && value.nodes.every(isSemanticNode);
        case "pingo:layout-geometry":
            return isLayoutGeometryFrame(value.frame);
        case "pingo:painted-text":
            return isPaintedTextSnapshot(value.snapshot);
        case "pingo:fatal":
            return typeof value.error === "string";
        case "pingo:shutdown-complete":
            return true;
        default:
            return false;
    }
}
export function isRenderWorkerOutboundEnvelope(value) {
    if (!isRecord(value))
        return false;
    return (value.kind === "pingo:prepared" ||
        value.kind === "pingo:ready" ||
        value.kind === "pingo:frame" ||
        value.kind === "pingo:clock-metrics" ||
        value.kind === "pingo:virtual-refill" ||
        value.kind === "pingo:edit-transaction" ||
        value.kind === "pingo:event-transaction" ||
        value.kind === "pingo:non-passive-regions" ||
        value.kind === "pingo:editing-geometry" ||
        value.kind === "pingo:semantics" ||
        value.kind === "pingo:layout-geometry" ||
        value.kind === "pingo:painted-text" ||
        value.kind === "pingo:fatal" ||
        value.kind === "pingo:shutdown-complete");
}
function isSemanticNode(value) {
    return (isRecord(value) &&
        isU32(value.nodeId) &&
        typeof value.focusable === "boolean" &&
        typeof value.focused === "boolean" &&
        typeof value.password === "boolean" &&
        typeof value.role === "string" &&
        typeof value.label === "string" &&
        typeof value.value === "string" &&
        !(value.password && value.value !== "") &&
        isEditingGeometryRect(value.bounds));
}
function isEditingGeometryRect(value) {
    return (isRecord(value) &&
        isFiniteNumber(value.left) &&
        isFiniteNumber(value.top) &&
        isFiniteNumber(value.width) &&
        isFiniteNumber(value.height) &&
        value.width >= 0 &&
        value.height >= 0);
}
function isEditingCharacterBounds(value) {
    return (isRecord(value) &&
        isU32(value.start) &&
        isU32(value.end) &&
        value.start < value.end &&
        isEditingGeometryRect(value.rect));
}
function isEditingGeometryFrame(value) {
    return (isRecord(value) &&
        isU32(value.nodeId) &&
        isU32(value.selectionStart) &&
        isU32(value.selectionEnd) &&
        value.selectionStart <= value.selectionEnd &&
        isEditingGeometryRect(value.controlBounds) &&
        isEditingGeometryRect(value.selectionBounds) &&
        Array.isArray(value.characterBounds) &&
        value.characterBounds.every(isEditingCharacterBounds));
}
function isLayoutGeometryFrame(value) {
    return (isRecord(value) &&
        isU32(value.frameSeq) &&
        Array.isArray(value.records) &&
        value.records.every(isLayoutGeometryRecord));
}
function isPaintedTextSnapshot(value) {
    return (isRecord(value) &&
        typeof value.truncated === "boolean" &&
        Array.isArray(value.records) &&
        value.records.every(isPaintedTextRecord));
}
function isPaintedTextRecord(value) {
    return (isRecord(value) &&
        isU32(value.nodeId) &&
        (value.channel === "shapedRun" ||
            value.channel === "systemFallback" ||
            value.channel === "inlineFallback") &&
        typeof value.text === "string" &&
        typeof value.originClipped === "boolean" &&
        typeof value.unattributed === "boolean" &&
        isRecord(value.origin) &&
        typeof value.origin.x === "number" &&
        typeof value.origin.y === "number" &&
        !Number.isNaN(value.origin.x) &&
        !Number.isNaN(value.origin.y));
}
function isLayoutGeometryRecord(value) {
    return (isRecord(value) && isU32(value.nodeId) && isLayoutRect(value.bounds) && isLayoutRect(value.clip));
}
/**
 * Like {@link isEditingGeometryRect} but tolerates infinities.
 *
 * An unclipped node reports an unbounded clip box, so requiring finiteness here
 * would reject the common case. NaN is still refused — it survives every
 * comparison a placement strategy would make.
 */
function isLayoutRect(value) {
    return (isRecord(value) &&
        isNonNaNNumber(value.left) &&
        isNonNaNNumber(value.top) &&
        isNonNaNNumber(value.width) &&
        isNonNaNNumber(value.height) &&
        value.width >= 0 &&
        value.height >= 0);
}
function isNonNaNNumber(value) {
    return typeof value === "number" && !Number.isNaN(value);
}
function isNonPassiveRegion(value) {
    return (isRecord(value) &&
        isU32(value.flags) &&
        value.flags >= 1 &&
        value.flags <= 3 &&
        isFiniteNumber(value.left) &&
        isFiniteNumber(value.top) &&
        isFiniteNumber(value.right) &&
        isFiniteNumber(value.bottom) &&
        value.left < value.right &&
        value.top < value.bottom);
}
function isEventTransaction(value) {
    return (isRecord(value) &&
        isU32(value.eventId) &&
        isInputEventKind(value.kind) &&
        isU32(value.target) &&
        isFiniteNumber(value.x) &&
        isFiniteNumber(value.y) &&
        isFiniteNumber(value.deltaX) &&
        isFiniteNumber(value.deltaY) &&
        isU32(value.buttons) &&
        value.buttons <= 0xffff &&
        isU32(value.modifiers) &&
        value.modifiers <= 0x0f &&
        isU32(value.pointerId) &&
        isU32(value.elapsedMicros) &&
        value.elapsedMicros >= 1 &&
        value.elapsedMicros <= 1_000_000 &&
        (value.relatedTarget === null || isU32(value.relatedTarget)) &&
        (value.pointerType === "none" ||
            value.pointerType === "mouse" ||
            value.pointerType === "pen" ||
            value.pointerType === "touch") &&
        typeof value.isPrimary === "boolean" &&
        isFiniteNumber(value.pressure) &&
        value.pressure >= 0 &&
        value.pressure <= 1 &&
        isFiniteNumber(value.tiltX) &&
        value.tiltX >= -90 &&
        value.tiltX <= 90 &&
        isFiniteNumber(value.tiltY) &&
        value.tiltY >= -90 &&
        value.tiltY <= 90 &&
        isFiniteNumber(value.width) &&
        value.width >= 0 &&
        isFiniteNumber(value.height) &&
        value.height >= 0 &&
        Array.isArray(value.path) &&
        value.path.length > 0 &&
        value.path.every(isU32) &&
        new Set(value.path).size === value.path.length &&
        value.path.at(-1) === value.target);
}
function isStructureRequest(value) {
    return (isRecord(value) &&
        isU32(value.nodeId) &&
        isU32(value.sequence) &&
        (value.kind === "merge" || value.kind === "remove" || value.kind === "split") &&
        isU32(value.target) &&
        isU32(value.source) &&
        isU32(value.offset) &&
        Array.isArray(value.keys) &&
        value.keys.every((key) => isU32(key)));
}
function isDocumentSelectionReport(value) {
    if (!isRecord(value) || !isU32(value.nodeId) || !isRecord(value.selection))
        return false;
    const selection = value.selection;
    if (selection.kind === "text") {
        return (isU32(selection.anchorKey) &&
            isU32(selection.anchorOffset) &&
            isU32(selection.focusKey) &&
            isU32(selection.focusOffset));
    }
    if (selection.kind === "node")
        return isU32(selection.key);
    return selection.kind === "gap" && isU32(selection.index);
}
function isEditTransaction(value) {
    if (!isRecord(value) ||
        !isNonNegativeInteger(value.nodeId) ||
        value.nodeId > 0xffff_ffff ||
        typeof value.baseRevision !== "bigint" ||
        typeof value.revision !== "bigint" ||
        value.baseRevision < 0n ||
        value.revision <= value.baseRevision ||
        !isRecord(value.selection) ||
        !isU32(value.selection.anchor) ||
        !isU32(value.selection.focus) ||
        !isAffinity(value.selection.anchorAffinity) ||
        !isAffinity(value.selection.focusAffinity) ||
        !isTransactionKind(value.kind)) {
        return false;
    }
    if (value.delta !== undefined) {
        if (!isRecord(value.delta) ||
            typeof value.delta.text !== "string" ||
            !isRange(value.delta.range)) {
            return false;
        }
    }
    return value.composition === undefined || isRange(value.composition);
}
function isRange(value) {
    return isRecord(value) && isU32(value.start) && isU32(value.end) && value.start <= value.end;
}
function isU32(value) {
    return isNonNegativeInteger(value) && value <= 0xffff_ffff;
}
function isAffinity(value) {
    return value === "upstream" || value === "downstream";
}
function isTransactionKind(value) {
    return (value === "edit" ||
        value === "composition" ||
        value === "undo" ||
        value === "redo" ||
        value === "external");
}
function isVirtualRefillRange(value) {
    return (isRecord(value) &&
        isNonNegativeInteger(value.nodeId) &&
        value.nodeId <= 0xffff_ffff &&
        isNonNegativeInteger(value.start) &&
        value.start <= 0xffff_ffff &&
        isNonNegativeInteger(value.end) &&
        value.end <= 0xffff_ffff &&
        value.start < value.end);
}
function isFrameReport(value) {
    if (!isRecord(value))
        return false;
    if (!isNonNegativeInteger(value.commands) ||
        !isNonNegativeInteger(value.pictures) ||
        !isNonNegativeInteger(value.maximumPictureDepth) ||
        !isNonNegativeInteger(value.mutationBytes) ||
        !isNonNegativeInteger(value.displayListBytes))
        return false;
    if (value.cause !== undefined &&
        value.cause !== "mutation" &&
        value.cause !== "input" &&
        value.cause !== "animation" &&
        value.cause !== "media")
        return false;
    if (value.cause === "media"
        ? value.mediaPath !== "image-bitmap" && value.mediaPath !== "video-frame"
        : value.mediaPath !== undefined)
        return false;
    if (value.inputBytes !== undefined && !isNonNegativeInteger(value.inputBytes))
        return false;
    if (value.animationDeltaMs !== undefined && !isNonNegativeFinite(value.animationDeltaMs))
        return false;
    if (value.core !== undefined && !isCoreDiagnostics(value.core))
        return false;
    if (value.rasterCache !== undefined && !isRasterMetrics(value.rasterCache))
        return false;
    return value.rasterFrame === undefined || isRasterFrame(value.rasterFrame);
}
function isCoreDiagnostics(value) {
    if (!isRecord(value))
        return false;
    const counters = [
        value.frameSeq,
        value.sceneNodes,
        value.dirtyLayoutNodes,
        value.dirtyPaintNodes,
        value.dirtyPaintSelfNodes,
        value.dirtyHitNodes,
        value.dirtySemanticsNodes,
        value.layoutChangedNodes,
        value.layoutVisitedNodes,
        value.displayCommands,
        value.pictureBuilds,
        value.pictureCacheHits,
        value.pictureSubtreeBuilds,
        value.pictureSubtreeCacheHits,
        value.overInvalidatedFrames,
    ];
    return (counters.every(isNonNegativeInteger) &&
        typeof value.paintRebuilt === "boolean" &&
        typeof value.pictureHash === "bigint" &&
        value.pictureHash >= 0n);
}
function isRasterMetrics(value) {
    if (!isRecord(value))
        return false;
    return [
        value.budgetBytes,
        value.bypassedFrames,
        value.bytes,
        value.compositedTiles,
        value.entries,
        value.evictions,
        value.hits,
        value.misses,
    ].every(isNonNegativeInteger);
}
function isRasterFrame(value) {
    return (isRecord(value) &&
        typeof value.bypassed === "boolean" &&
        isNonNegativeInteger(value.hits) &&
        isNonNegativeInteger(value.misses));
}
function isClockMetrics(value) {
    if (!isRecord(value))
        return false;
    return (isNonNegativeInteger(value.acceptedAnchors) &&
        isNonNegativeInteger(value.anchoredFrames) &&
        isNonNegativeInteger(value.frames) &&
        isNonNegativeInteger(value.ignoredAnchors) &&
        isNonNegativeFinite(value.maximumFrameGapMs) &&
        isNonNegativeInteger(value.overruns) &&
        typeof value.running === "boolean" &&
        isNonNegativeInteger(value.selfDrivenFrames));
}
function isWorkerMode(value) {
    return value === "post-message" || value === "sab";
}
function isRecord(value) {
    return typeof value === "object" && value !== null;
}
function isPositiveU32(value) {
    return Number.isInteger(value) && value >= 1 && value <= 0xffff_ffff;
}
function isNonNegativeInteger(value) {
    return Number.isSafeInteger(value) && value >= 0;
}
function isNonNegativeFinite(value) {
    return typeof value === "number" && Number.isFinite(value) && value >= 0;
}
function isPositiveFinite(value) {
    return typeof value === "number" && Number.isFinite(value) && value > 0;
}
function isFiniteNumber(value) {
    return typeof value === "number" && Number.isFinite(value);
}
function isSharedArrayBuffer(value) {
    return typeof SharedArrayBuffer === "function" && value instanceof SharedArrayBuffer;
}
//# sourceMappingURL=worker-protocol.js.map